import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Observable } from 'rxjs';

export interface AttendanceCheckOutDialogData {
  /** When the user checked in. */
  startTimeIso: string;
  /** End time to pre-fill: now for a normal check-out, a capped time for a stale session. */
  suggestedEnd: Date;
  /**
   * True when the session ran past midnight or past the maximum session length — the user
   * almost certainly left without checking out, so we ask them when rather than guessing.
   */
  stale: boolean;
  /** Where they checked in, e.g. "Office (±18m)". Absent if location was never resolved. */
  checkInPlace?: string;
  /** Where they are checking out, resolving while the dialog is open. */
  checkOutPlace$: Observable<string | undefined>;
}

/**
 * Confirms a check-out. Always shows in-time, out-time and the resulting total, because
 * this is an attendance record people are held to. The out-time is editable so a forgotten
 * check-out can be corrected to when the person actually left.
 */
@Component({
    selector: 'app-attendance-check-out-dialog',
    imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule],
    templateUrl: './attendance-check-out-dialog.component.html',
    styleUrls: ['./attendance-check-out-dialog.component.scss']
})
export class AttendanceCheckOutDialogComponent {
  readonly data: AttendanceCheckOutDialogData = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<AttendanceCheckOutDialogComponent>);

  readonly start = new Date(this.data.startTimeIso);

  /** Bound to <input type="time"> — always "HH:mm" on the check-in day. */
  endTime = this.toTimeInput(this.data.suggestedEnd);

  get end(): Date {
    const [h, m] = (this.endTime || '').split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return this.start;
    // Anchored to the check-in date: a working day is recorded against the day it began.
    return new Date(this.start.getFullYear(), this.start.getMonth(), this.start.getDate(), h, m, 0);
  }

  get totalMinutes(): number {
    return Math.round((this.end.getTime() - this.start.getTime()) / 60000);
  }

  get isValid(): boolean {
    return this.totalMinutes > 0;
  }

  get totalLabel(): string {
    const total = this.totalMinutes;
    if (total <= 0) return '—';
    return `${Math.floor(total / 60)}h ${this.pad(total % 60)}m`;
  }

  confirm(): void {
    if (!this.isValid) return;
    this.dialogRef.close(this.end);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  private toTimeInput(date: Date): string {
    return `${this.pad(date.getHours())}:${this.pad(date.getMinutes())}`;
  }

  private pad(n: number): string {
    return n.toString().padStart(2, '0');
  }
}
